import { IconGrid } from "@/assets/icons/geist/IconGrid";
import { IconCode } from "@/assets/icons/geist/IconCode";
import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import { useRunnerUsage } from "@/hooks/use-canonical-domain-runners";
import { pluralizeWithCount } from "@/utils/strings";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";

export type RunnerUsageButtonsProps = {
    /** Configuration for local runner usage */
    localRunner?: boolean;
    /** Specific runner ID for cloud runners */
    runnerId?: string;
    /** Custom className for the container */
    className?: string;
    /** Test ID prefix for buttons */
    testIdPrefix?: string;
};

export const RunnerUsageButtons: FC<RunnerUsageButtonsProps> = ({
    localRunner = false,
    runnerId,
    className,
    testIdPrefix = "runner-usage",
}) => {
    const navigate = useNavigate();

    const {
        projectCount: numProjects,
        environmentCount: numEnvironments,
        hasMoreEnvironments: hasNextPageEnvironments,
        isLoading,
    } = useRunnerUsage({
        runnerId: !localRunner ? runnerId : undefined,
        runnerKinds: localRunner ? [RunnerKind.LOCAL] : undefined,
    });


    // Generate navigation URLs
    const projectsUrl = localRunner ? "/projects?environmentClass=local" : `/projects?runnerId=${runnerId}`;

    const environmentsUrl = localRunner
        ? "/settings/environments?runner=local"
        : `/settings/environments?runner=${runnerId}`;

    return (
        <div className={className}>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <Text className="text-sm font-bold text-content-secondary">Used by</Text>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                    <Button
                        LeadingIcon={IconGrid}
                        size="sm"
                        variant="secondary"
                        disabled={numProjects === 0}
                        loading={isLoading}
                        onClick={() => navigate(projectsUrl)}
                        className="w-full sm:w-auto"
                        data-testid={`${testIdPrefix}-projects-button`}
                        data-tracking-id="view-runner-projects-runner-usage-buttons"
                    >
                        {pluralizeWithCount(numProjects, false, "Project")}
                    </Button>
                    <Button
                        LeadingIcon={IconCode}
                        size="sm"
                        variant="secondary"
                        disabled={numEnvironments === 0}
                        loading={isLoading}
                        onClick={() => navigate(environmentsUrl)}
                        className="w-full sm:w-auto"
                        data-testid={`${testIdPrefix}-environments-button`}
                        data-tracking-id="view-runner-environments-runner-usage-buttons"
                    >
                        {pluralizeWithCount(numEnvironments, hasNextPageEnvironments, "Environment")}
                    </Button>
                </div>
            </div>
        </div>
    );
};
